import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/use-toast'
import { Loader2 } from 'lucide-react'
import { useUpdateProfile } from '@/lib/api/services/user-service'
import FileUploader from '@/components/shared/FileUploader'
import CdnImage from '@/components/shared/CdnImage'
import { DEFAULT_AVATAR } from '@/constants'
import { api } from '@/lib/api/axios-instance'

// Define the type for the form data - simplified structure
interface ProfileFormData {
  firstname: string
  lastname: string
  email: string
  cin: string
  birthday: string
  gender: string
  address: string
  phone: string
  password: string
  nationality?: string
  avatar?: string
}

interface ProfileFormProps {
  onCancel?: () => void
}

export function ProfileForm({ onCancel }: ProfileFormProps) {
  const { t } = useTranslation('common')
  const { t: tNationalities } = useTranslation('nationalities')
  const { toast } = useToast()
  const updateProfile = useUpdateProfile()
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Helper function to get translated nationality
  const getTranslatedNationality = (nationalityKey: string | undefined): string => {
    if (!nationalityKey) return ''
    
    // Normalize the key to lowercase to match JSON keys
    const normalizedKey = nationalityKey.toLowerCase().trim()
    
    // Try to get the translation - the JSON structure is { "nationalities": { "tunisian": "..." } }
    // So within the "nationalities" namespace, we need to use "nationalities.tunisian"
    const translationKey = `nationalities.${normalizedKey}`
    const translated = tNationalities(translationKey)
    
    // Debug: log if translation is found
    if (translated === translationKey) {
      // Translation not found, try without the "nationalities." prefix
      const altKey = normalizedKey
      const altTranslated = tNationalities(altKey)
      if (altTranslated !== altKey) {
        return altTranslated
      }
      // If still not found, return original
      console.warn(`Nationality translation not found for key: ${nationalityKey} (normalized: ${normalizedKey})`)
      return nationalityKey
    }
    
    return translated
  }
  const [formData, setFormData] = useState<ProfileFormData>({
    firstname: '',
    lastname: '',
    email: '',
    cin: '',
    birthday: '',
    gender: '',
    address: '',
    phone: '',
    password: '',
    nationality: '',
  })
  // Fetch user data on component mount
  useEffect(() => { 
    const fetchUserData = async () => {
      try {
        setIsLoading(true)

        // Use axios instance with withCredentials: true (HTTP-only cookies)
        // The backend will validate the HTTP-only cookie and return user data
        // Note: Endpoint includes /server prefix as per backend API structure
        const response = await api.get('/user-controller/session')
        const userData = response.data
        // console.log('User data fetched:', userData)

        // Handle birthday formatting
        let formattedBirthday = ''
        if (userData.birthday) {
          const date = new Date(userData.birthday)
          if (!isNaN(date.getTime())) {
            // Store as simple YYYY-MM-DD format
            formattedBirthday = date.toISOString().split('T')[0]
          }
        }

        setFormData({
          firstname: userData.firstname || '',
          lastname: userData.lastname || '',
          email: userData.email || '',
          cin: userData.cin || '',
          birthday: formattedBirthday,
          gender: userData.gender || '',
          address: userData.address || '',
          phone: userData.phone || '',
          password: '',
          nationality: userData.nationality || '',
          avatar: userData.avatar || DEFAULT_AVATAR,
        })
        
        // Debug: Log avatar information
        // console.log('ProfileForm - Avatar data:', {
        //   hasAvatar: !!userData.avatar,
        //   avatar: userData.avatar,
        //   usingDefault: !userData.avatar,
        // })
      } catch (error: any) {
        // console.error('Error fetching user data:', error)
        
        // Handle 401 (Unauthorized) - cookie expired or invalid
        if (error?.response?.status === 401) {
          toast({
            title: 'Authentication Required',
            description: 'Your session has expired. Please log in again.',
            variant: 'destructive',
          })
          // Optionally redirect to login after a short delay
          setTimeout(() => {
            window.location.href = '/login'
          }, 2000)
        } else {
          // Other errors
          const errorMessage = error?.response?.data?.message || error?.message || t('profileForm.notifications.loadError')
          toast({
            title: t('debug-folder.tokenDebugger.error.title'),
            description: errorMessage,
            variant: 'destructive',
          })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchUserData()
  }, [toast, t])

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Handle avatar upload
  const handleAvatarUpload = (url: string) => {
    setFormData((prev) => ({ ...prev, avatar: url }))
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Prevent multiple submissions
    if (isSubmitting || updateProfile.isPending) {
      return
    }

    try {
      setIsSubmitting(true)

      // Create a copy of the form data with optional password
      // Note: email, cin, and nationality are read-only and not included in updates
      const dataToSubmit: {
        firstname: string
        lastname: string
        gender: string
        address: string
        phone: string
        password?: string
        avatar?: string
      } = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        gender: formData.gender,
        address: formData.address,
        phone: formData.phone,
        avatar: formData.avatar,
      }

      // Add password only if it's provided and not empty
      if (formData.password && formData.password.trim() !== '') {
        dataToSubmit.password = formData.password
      }

      // 🔍 DEBUG: Log the exact data being sent to backend
      // console.log('🚀 SENDING TO BACKEND:', JSON.stringify(dataToSubmit, null, 2))
      // console.log('📊 Data fields included:', Object.keys(dataToSubmit))
      // console.log('🔐 Password included:', !!dataToSubmit.password)

      // Submit the form data
      await updateProfile.mutateAsync(dataToSubmit)

      if (onCancel) {
        onCancel()
      }
    } catch (error) {
      // console.error('❌ Error updating profile:', error)
      // Error handling is done in the hook
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* First Name */}
        <div className="space-y-2">
          <label htmlFor="firstname" className="text-sm font-medium">
            {t('profileForm.fields.firstName')}
          </label>
          <Input
            id="firstname"
            name="firstname"
            value={formData.firstname}
            onChange={handleChange}
            placeholder={t('profileForm.placeholders.firstName')}
            required
          />
        </div>

        {/* Last Name */}
        <div className="space-y-2">
          <label htmlFor="lastname" className="text-sm font-medium">
            {t('profileForm.fields.lastName')}
          </label>
          <Input
            id="lastname"
            name="lastname"
            value={formData.lastname}
            onChange={handleChange}
            placeholder={t('profileForm.placeholders.lastName')}
            required
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">
            {t('profileForm.fields.email')}
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            placeholder={t('profileForm.placeholders.email')}
            readOnly
            disabled
            className="bg-gray-100 cursor-not-allowed"
          />
        </div>

        {/* CIN */}
        <div className="space-y-2">
          <label htmlFor="cin" className="text-sm font-medium">
            {t('profileForm.fields.cin')}
          </label>
          <Input
            id="cin"
            name="cin"
            value={formData.cin}
            placeholder={t('profileForm.placeholders.cin')}
            readOnly
            disabled
            className="bg-gray-100 cursor-not-allowed"
          />
        </div>

        {/* Birthday */}
        <div className="space-y-2">
          <label htmlFor="birthday" className="text-sm font-medium">
            {t('profileForm.fields.birthday')}
          </label>
          <Input
            id="birthday"
            name="birthday"
            type="date"
            value={formData.birthday}
            placeholder={t('profileForm.placeholders.birthday')}
            readOnly
            disabled
            className="bg-gray-100 cursor-not-allowed"
          />
        </div>

        {/* Gender */}
        <div className="space-y-2">
          <label htmlFor="gender" className="text-sm font-medium">
            {t('profileForm.fields.gender')}
          </label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            required
          >
            <option value="MALE">{t('profileForm.genderOptions.male')}</option>
            <option value="FEMALE">{t('profileForm.genderOptions.female')}</option>
          </select>
        </div>

        {/* Address */}
        <div className="space-y-2">
          <label htmlFor="address" className="text-sm font-medium">
            {t('profileForm.fields.address')}
          </label>
          <Input
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder={t('profileForm.placeholders.address')}
          />
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm font-medium">
            {t('profileForm.fields.phone')}
          </label>
          <Input
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder={t('profileForm.placeholders.phone')}
          />
        </div>

        {/* Nationality */}
        <div className="space-y-2">
          <label htmlFor="nationality" className="text-sm font-medium">
            {t('profileForm.fields.nationality')}
          </label>
          <Input
            id="nationality"
            name="nationality"
            value={getTranslatedNationality(formData.nationality)}
            placeholder={t('profileForm.placeholders.nationality')}
            readOnly
            disabled
            className="bg-gray-100 cursor-not-allowed"
          />
        </div>

        {/* Password */}
        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium">
            {t('profileForm.fields.password')}
          </label>
          <Input
            id="password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            placeholder={t('profileForm.placeholders.password')}
          />
        </div>

        {/* Avatar */}
        <div className="space-y-2 col-span-full">
          <FileUploader
            onFileUploaded={handleAvatarUpload}
            onError={() => {
              // console.error('Upload error:', error)
              toast({
                title: t('debug-folder.tokenDebugger.error.title'),
                description: t('profileForm.notifications.error'),
                variant: 'destructive',
              })
            }}
            previewUrl={formData.avatar && formData.avatar !== DEFAULT_AVATAR ? formData.avatar : undefined}
            isAvatar={true}
            label="Profile Picture"
            defaultPreview={formData.avatar || DEFAULT_AVATAR}
            previewComponent={(url) => (
              <CdnImage
                src={url}
                alt="Profile Picture"
                className="h-full w-full object-cover"
                fallbackSrc={DEFAULT_AVATAR}
              />
            )}
          />
        </div>
      </div>

      <div className="flex justify-end gap-4 pt-4">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting || updateProfile.isPending}
          >
            {t('profileForm.buttons.cancel')}
          </Button>
        )}
        <Button 
          type="submit" 
          disabled={isSubmitting || updateProfile.isPending}
        >
          {(isSubmitting || updateProfile.isPending) ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('profileForm.buttons.updating')}
            </>
          ) : (
            t('profileForm.buttons.update')
          )}
        </Button>
      </div>
    </form>
  )
}
